import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, X, Trash2, Building2, User2, IndianRupee, GripVertical, UserCheck, Home, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";
import PropertyDetailsModal from "./PropertyDetailsModal";

const STAGES = ["Initiation", "Negotiation", "Conveyance", "Handover", "Completion"];
const BOARD_META = {
  sale: { title: "Sale", sub: "Track properties through the sale lifecycle." },
  purchase: { title: "Purchase", sub: "Manage acquisitions from initiation to completion." },
  tenancy: { title: "Tenancy", sub: "Oversee lettings through each stage." },
  maintenance: { title: "Maintenance", sub: "Manage maintenance work through each stage." },
};
const STAGE_ACCENT = {
  Initiation: "border-t-blue-500", Negotiation: "border-t-amber-500",
  Conveyance: "border-t-violet-500", Handover: "border-t-emerald-500", Completion: "border-t-teal-500",
};
const PRIORITY = {
  High: "text-rose-700 bg-rose-50 border-rose-200",
  Medium: "text-amber-700 bg-amber-50 border-amber-200",
  Low: "text-slate-600 bg-slate-100 border-slate-200",
};
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const SELECT = "bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const INPUT = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

export default function KanbanBoard({ board }) {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "power_user";
  const meta = BOARD_META[board];
  const { clients, selectedClientId, canSelectClient } = usePortal();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [properties, setProperties] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [details, setDetails] = useState(null);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c.name]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = canSelectClient && selectedClientId ? `&client_id=${selectedClientId}` : "";
      const { data } = await api.get(`/deals?board=${board}${q}`);
      setDeals(data);
      const pq = canSelectClient && selectedClientId ? `?client_id=${selectedClientId}` : "";
      const pres = await api.get(`/properties${pq}`);
      setProperties(pres.data);
    } catch (e) { toast.error("Failed to load board"); }
    finally { setLoading(false); }
  }, [board, canSelectClient, selectedClientId]);
  useEffect(() => { load(); }, [load]);

  const moveDeal = async (id, stage) => {
    const prev = deals;
    const target = deals.find((d) => d.id === id);
    if (!target || target.stage === stage) return;
    setDeals(deals.map((d) => (d.id === id ? { ...d, stage } : d)));
    try { await api.patch(`/deals/${id}`, { stage }); }
    catch (e) { setDeals(prev); toast.error("Could not move card"); }
  };

  const reassign = async (id, clientId) => {
    const prev = deals;
    setDeals(deals.map((d) => (d.id === id ? { ...d, client_id: clientId || null } : d)));
    try {
      await api.patch(`/deals/${id}`, { client_id: clientId || "" });
      toast.success(clientId ? `Assigned to ${clientMap[clientId]}` : "Unassigned");
    } catch (e) { setDeals(prev); toast.error("Could not reassign"); }
  };

  const onDrop = (stage) => { setDragOver(null); if (dragId) moveDeal(dragId, stage); setDragId(null); };

  const openDetails = (deal) => {
    let pid = deal.property_id;
    if (!pid) {
      const match = properties.find((p) => (p.address || "").toLowerCase() === (deal.property_address || "").toLowerCase());
      pid = match?.id;
    }
    if (!pid) { toast.error("No property linked to this card"); return; }
    setDetails({ id: pid });
  };

  const remove = async (id) => {
    try { await api.delete(`/deals/${id}`); setDeals(deals.filter((d) => d.id !== id)); toast.success("Card removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addFromProperty = async (prop) => {
    const owner = (prop.owners && prop.owners[0]) ? `${prop.owners[0].first_name} ${prop.owners[0].last_name}` : "";
    try {
      const { data } = await api.post("/deals", {
        board, stage: "Initiation", title: prop.address,
        property_address: prop.address, value: prop.valuation || 0,
        client_name: owner, priority: "Medium", client_id: prop.client_id || null,
        property_id: prop.id,
      });
      setDeals((prev) => [...prev, data]);
      setShowPicker(false);
      toast.success(`"${prop.address}" added to Initiation`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add property"); }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Board</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">{meta.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{meta.sub}</p>
        </div>
        {canManage && (
          <button onClick={() => setShowPicker(true)} data-testid="add-property-to-pipeline-button"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg px-5 py-2.5 transition-all shadow-sm active:scale-[0.98]">
            <Home className="h-4 w-4" /> Add Property
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="mt-8 overflow-x-auto pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 min-w-[1000px] lg:min-w-0">
            {STAGES.map((stage) => {
              const cards = deals.filter((d) => d.stage === stage);
              return (
                <div key={stage} data-testid={`kanban-column-${stage.toLowerCase()}`}
                  onDragOver={(e) => { if (!canManage) return; e.preventDefault(); setDragOver(stage); }}
                  onDragLeave={() => setDragOver((s) => (s === stage ? null : s))}
                  onDrop={() => canManage && onDrop(stage)}
                  className={`rounded-2xl bg-slate-100 border-t-2 ${STAGE_ACCENT[stage]} border border-slate-200 p-3 transition-colors ${dragOver === stage ? "bg-emerald-50 border-emerald-300" : ""}`}>
                  <div className="flex items-center justify-between px-1 py-2">
                    <span className="text-sm font-semibold text-slate-800">{stage}</span>
                    <span className="text-xs font-mono text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{cards.length}</span>
                  </div>
                  <div className="space-y-3 mt-1 min-h-[80px]">
                    {cards.map((d) => (
                      <div key={d.id} data-testid="kanban-card"
                        draggable={canManage}
                        onClick={() => openDetails(d)}
                        onDragStart={() => canManage && setDragId(d.id)}
                        onDragEnd={() => { setDragId(null); setDragOver(null); }}
                        className={`group rounded-xl bg-white border border-slate-200 shadow-sm p-4 transition-all duration-200 hover:border-emerald-300 hover:shadow-md cursor-pointer ${canManage ? "active:cursor-grabbing" : ""} ${dragId === d.id ? "opacity-50" : ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-1.5 min-w-0">
                            {canManage && <GripVertical className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />}
                            <h4 className="font-semibold text-sm leading-snug truncate text-slate-900">{d.title}</h4>
                          </div>
                          {canManage && (
                            <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} data-testid="delete-deal-button"
                              className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {d.property_address && (
                          <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Building2 className="h-3 w-3 shrink-0" /> {d.property_address}</p>
                        )}
                        {d.client_name && (
                          <p className="mt-1 text-xs text-slate-500 flex items-center gap-1.5"><User2 className="h-3 w-3 shrink-0" /> {d.client_name}</p>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xs font-mono text-emerald-700 flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" /> {inr(d.value)}
                          </span>
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${PRIORITY[d.priority] || PRIORITY.Medium}`}>{d.priority}</span>
                        </div>
                        {d.client_id && clientMap[d.client_id] ? (
                          <div className="mt-3 pt-2 border-t border-slate-200 text-[10px] font-mono text-emerald-700 flex items-center gap-1">
                            <UserCheck className="h-3 w-3" /> {clientMap[d.client_id]}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {cards.length === 0 && canManage && <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-300 rounded-lg">Drop here</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showPicker && <PropertyPickerModal properties={properties} existing={deals} onSelect={addFromProperty} onClose={() => setShowPicker(false)} />}
      {details && <PropertyDetailsModal propertyId={details.id} defaultTab={board} onClose={() => setDetails(null)} onUpdated={load} />}
    </div>
  );
}

function AddDealModal({ board, clients, onClose, onAdded }) {
  const [form, setForm] = useState({ title: "", property_address: "", client_name: "", value: "", priority: "Medium", stage: "Initiation", client_id: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/deals", { ...form, board, value: Number(form.value) || 0, client_id: form.client_id || null });
      toast.success("Deal added");
      onAdded(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-deal-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-900">New Deal</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <In label="Title" testid="deal-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
          <In label="Property address" testid="deal-address-input" value={form.property_address} onChange={(v) => setForm({ ...form, property_address: v })} />
          <In label="Client label (display)" testid="deal-client-input" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
          <div className="grid grid-cols-2 gap-4">
            <In label="Value (£)" type="number" testid="deal-value-input" value={form.value} onChange={(v) => setForm({ ...form, value: v })} />
            <div>
              <label className="text-sm text-slate-600">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="deal-priority-select" className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
                {["High", "Medium", "Low"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-600">Stage</label>
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="deal-stage-select" className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
                {STAGES.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600">Assign to client</label>
              <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="deal-assign-client-select" className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
                <option value="">Unassigned</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="save-deal-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add Deal
          </button>
        </form>
      </div>
    </div>
  );
}

function PropertyPickerModal({ properties, existing, onSelect, onClose }) {
  const inPipeline = new Set(existing.map((d) => (d.property_address || "").toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="property-picker-modal">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-slate-900">Add Property to Pipeline</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-5">Select a property from My Properties — it will be added to the <span className="font-medium text-slate-700">Initiation</span> stage.</p>
        {properties.length === 0 ? (
          <div className="p-8 text-center text-slate-500 border border-dashed border-slate-300 rounded-xl">
            <Home className="h-8 w-8 mx-auto text-slate-400" />
            <p className="mt-3 text-sm">No properties found. Add one in <span className="font-medium">My Properties</span> first.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="property-picker-list">
            {properties.map((p) => {
              const added = inPipeline.has((p.address || "").toLowerCase());
              return (
                <button key={p.id} type="button" disabled={added}
                  onClick={() => onSelect(p)} data-testid="property-picker-item"
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${added ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50"}`}>
                  <span className="h-11 w-11 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                    {(p.image || (p.photos && p.photos[0])) && <img src={p.image || p.photos[0]} alt="" className="h-full w-full object-cover" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-slate-900 truncate">{p.address}</span>
                    <span className="block text-xs text-slate-500 font-mono">{p.type} · {inr(p.valuation)}</span>
                  </span>
                  {added
                    ? <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><Check className="h-3 w-3" /> In pipeline</span>
                    : <Plus className="h-4 w-4 text-emerald-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const In = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="text-sm text-slate-600">{label}</label>
    <input type={type} required={required} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} />
  </div>
);
